import { useState, useEffect } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { appId } from '../constants/appConstants';

/**
 * @description Hook customizado para buscar e gerenciar os dados de trocas do Firestore.
 *
 * @param {object} db - A instância do Firestore.
 * @param {object} user - O objeto do usuário autenticado. Se o usuário não estiver
 * logado, o hook não buscará os dados.
 *
 * @returns {object} Um objeto contendo:
 * - `trades` (Array): A lista de documentos de trocas.
 * - `loading` (boolean): Um indicador que se torna `true` enquanto os dados iniciais
 * estão sendo carregados.
 */
export const useTrades = (db, user) => {
    // Estado para armazenar a lista de trocas. Inicializado como um array vazio.
    const [trades, setTrades] = useState([]);
    // Estado para controlar o carregamento inicial dos dados.
    const [loading, setLoading] = useState(true);

    // useEffect para configurar o listener do Firestore.
    // Ele será executado quando o componente for montado ou quando 'db' ou 'user' mudarem.
    useEffect(() => {
        // Se não houver uma instância do DB ou um usuário logado, não faz nada.
        // Isso previne erros e chamadas desnecessárias à base de dados.
        if (!db || !user) {
            setLoading(false); // Para o carregamento se não houver usuário.
            return;
        }

        // Define a referência para a coleção 'trades' no Firestore.
        // A estrutura do caminho segue o padrão da aplicação para dados públicos.
        const tradesCollectionRef = collection(db, `artifacts/${appId}/public/data/trades`);

        // Cria uma query para a coleção. No futuro, podemos adicionar 'where' ou 'orderBy' aqui.
        const q = query(tradesCollectionRef);

        // onSnapshot estabelece uma conexão em tempo real com a coleção.
        // Qualquer alteração (adição, remoção, modificação) nos dados irá disparar este callback.
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const tradesData = [];
            // Itera sobre cada documento retornado pela snapshot.
            querySnapshot.forEach((doc) => {
                // Adiciona o ID do documento e os dados (`...doc.data()`) ao nosso array.
                tradesData.push({ id: doc.id, ...doc.data() });
            });
            // Atualiza o estado com os novos dados.
            setTrades(tradesData);
            // Define o carregamento como falso, pois os dados foram recebidos.
            setLoading(false);
        }, (error) => {
            // Em caso de erro ao buscar os dados (ex: problemas de permissão).
            console.error("Erro ao buscar dados de trocas: ", error);
            setLoading(false); // Para o carregamento mesmo se houver erro.
        });

        // Função de limpeza: é executada quando o componente que usa o hook é desmontado.
        // Isso cancela a inscrição do listener, evitando vazamentos de memória e cobranças desnecessárias.
        return () => unsubscribe();

    }, [db, user]); // Array de dependências: o efeito re-executa se 'db' ou 'user' mudarem.

    // Retorna os dados e o estado de carregamento para serem usados pelos componentes.
    return { trades, loading };
};
